import React, { useState } from 'react';

import { styled } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

import { SdmTypography, RHFUploadMultiFile, SdmSpacer } from 'components';
import SimpleRichText from './SimpleRichText';
import CustomTextFieldComponent from './CustomTextFieldComponent';
import { IconButton } from '@material-ui/core';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import LabelContainer from '../additionalComponents/LabelContainer';
import FileCropperDialog from '../dialogs/FileCropperDialog';

const variants = {
  simpleFile: {
    display: 'flex',
    justifyContent: 'space-between',
    '& div': {
      margin: 0,
    },
  },
  multipleFiles: {
    '& > div': {
      display: 'flex',
      flexWrap: 'wrap',
    },
    display: 'flex',
    flexDirection: 'column',
  },
};

const CustomImageContainer = styled('div')(({ variant = 'simpleFile' }) => ({
  ...variants[variant],
}));

const DropZonePreviewImage = ({
  control,
  error,
  name,
  altName,
  captionName = '',
  accept,
  watch,
  setValue,
  specs,
  handleChange,
  isMultiple,
  defaultValue = [],
  altDefaultValue = '',
  captionDefaultValue = '',
  captionLabel = '',
  captionDescription = '',
  altDescription = '',
  getValues,
  maxResolution,
  disableImg = false,
  disableAlt = false,
  disableCaption = false,
  setCustomErrors,
}) => {
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const wFile = watch(name);
  const [cropperDialog, setCropperDialog] = useState({
    isOpen: false,
    img: false,
  });

  const onRemove = (index) => {
    const newValues = wFile.filter((value, i) => index !== i);
    setValue(name, newValues);
    setValue(`alt_${name}_0`, '');
  };

  const cropBeforeDrop = (file) => {
    setCropperDialog({
      isOpen: true,
      i18n: i18n.info.cropper,
      img: file,
    });
  };

  const handleCroppedUpload = (file) => {
    Object.assign(file, {
      path: file.name,
    });
    file && file.name && file.type && handleChange([file]);
  };

  const handleMinSizeError = ({ files, minHeight, minWidth }) => {
    var _URL = window.URL || window.webkitURL;
    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const img = new Image();
            var objectUrl = _URL.createObjectURL(file);
            img.onload = function () {
              const fileHeight = img.height;
              const fileWidth = img.width;
              const hasSizeError =
                fileHeight < minHeight || fileWidth < minWidth;
              if (hasSizeError) {
                return setCustomErrors({
                  i18n: {
                    title: i18n.errors.width.title,
                    body: `${i18n.imageSpecs.coverSpecSize} ${minWidth}x${minHeight}px`,
                  },
                });
              }
              _URL.revokeObjectURL(objectUrl);
              resolve(file);
            };
            img.onerror = reject;
            img.src = objectUrl;
          })
      )
    ).then((files) => cropBeforeDrop(files));
  };

  // Renders
  const renderCropperDialog = () => {
    if (!cropperDialog.isOpen || !cropperDialog.img) return null;
    const fileMaxSize = maxResolution.split('x');
    const maxWidth = parseInt(fileMaxSize?.[0]);
    const maxHeight = parseInt(fileMaxSize?.[1]);
    return (
      <FileCropperDialog
        onClose={() => {
          setCropperDialog({ isOpen: false });
        }}
        i18n={{
          ...cropperDialog.i18n,
          agree: 'Ok',
        }}
        onAccept={handleCroppedUpload}
        img={cropperDialog.img}
        fixedWidth={maxWidth}
        fixedHeight={maxHeight}
      />
    );
  };

  return (
    <div>
      <RHFUploadMultiFile
        hideUploadedImages
        name={name}
        control={control}
        error={error}
        accept={accept}
        defaultValue={defaultValue}
        onDrop={(files) => {
          const fileMinSize = maxResolution.split('x');
          const minWidth = parseInt(fileMinSize?.[0]);
          const minHeight = parseInt(fileMinSize?.[1]);
          return handleMinSizeError({
            files,
            minHeight,
            minWidth,
          });
        }}
        onRemove={onRemove}
        isMultiple={isMultiple}
        dropZoneStyle={{ width: '553px', height: '81px' }}
        disabled={disableImg}
      />
      <div style={{ marginBottom: '15px', marginTop: '15px' }}>
        {specs.map((spec) => (
          <SdmTypography
            variant={'h5Regular'}
            style={{ margin: 0, fontStyle: 'italic' }}
            key={spec}
          >
            {spec}
          </SdmTypography>
        ))}
      </div>
      {renderCropperDialog()}
      {wFile?.length ? (
        <>
          <CustomImageContainer
            variant={isMultiple ? 'multipleFiles' : 'simpleFile'}
          >
            <div>
              {wFile.map(({ preview }, index) => (
                <div
                  style={{
                    marginRight: '15px',
                    marginTop: '5px',
                    maxWidth: '63px',
                    maxHeight: '63px',
                    position: 'relative',
                  }}
                  key={preview}
                >
                  {!disableImg ? (
                    <IconButton
                      edge="end"
                      size="small"
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        background: 'white',
                        transform: 'translate(50%, -50%)',
                      }}
                      onClick={() => onRemove(index)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                  <img
                    src={preview}
                    key={preview}
                    alt="preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                    }}
                  />
                </div>
              ))}
            </div>
            <div>
              <LabelContainer
                label={i18n.buttons.alt}
                description={altDescription || i18n.buttons.altDescription}
                hideSpacer
              />
              <Controller
                name={altName || `alt_${name}_0`}
                control={control}
                defaultValue={altDefaultValue || ''}
                render={(props) => {
                  return (
                    <CustomTextFieldComponent
                      label={i18n.buttons.alt}
                      placeholder={i18n.buttons.altPlaceholder}
                      variant="outlined"
                      style={{ width: '100%', alignSelf: 'end' }}
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      disabled={disableAlt}
                    />
                  );
                }}
              />
            </div>
          </CustomImageContainer>
          {captionName && (
            <>
              {captionLabel && (
                <>
                  <SdmSpacer height={25} />
                  <SdmTypography variant={'h4'} style={{ margin: 0 }}>
                    {captionLabel}
                  </SdmTypography>
                </>
              )}
              {captionDescription && (
                <SdmTypography variant={'h5Regular'} style={{ margin: 0 }}>
                  {captionDescription}
                </SdmTypography>
              )}
              <SimpleRichText
                control={control}
                name={captionName}
                getValues={getValues}
                defaultValue={captionDefaultValue}
                disabled={disableCaption}
              />
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

export default DropZonePreviewImage;
